/**
 * workLocationService — Work locations and employee groups.
 *
 * Owns:
 *   WorkLocation at /companies/{companyId}/workLocations/{locationId}
 *   EmployeeGroup at /companies/{companyId}/employeeGroups/{groupId}
 *
 * Rules: write restricted to `employer` role per database.rules.json.
 *
 * Public API:
 *   createWorkLocation, listWorkLocations,
 *   getWorkLocations, updateWorkLocation, deleteWorkLocation,
 *   createEmployeeGroup, getEmployeeGroups,
 *   updateEmployeeGroup, deleteEmployeeGroup, updateEmployeeGroup_Assignment
 *
 * Cross-cutting rules:
 *   - companyId is sourced from the authenticated session, never from a client param.
 *   - listWorkLocations returns newest-first; getWorkLocations returns active only.
 */

import {
  ref,
  push,
  set,
  get,
  update,
  query,
  orderByChild,
} from 'firebase/database';
import { getFirebase } from '../firebase';
import { byNewestCreated } from '../utils/recordDates';

// Dev-only logger — no-op in production builds.
const dbg = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

// ─── Path helpers ────────────────────────────────────────────────────────────

const workLocationsPath = (companyId) =>
  companyId && companyId !== 'legacy'
    ? `companies/${companyId}/workLocations`
    : 'workLocations';

const workLocationPath = (companyId, locationId) =>
  `${workLocationsPath(companyId)}/${locationId}`;

const employeeGroupsPath = (companyId) => `companies/${companyId}/employeeGroups`;

const employeeGroupPath = (companyId, groupId) =>
  `${employeeGroupsPath(companyId)}/${groupId}`;

// ─── Validation ───────────────────────────────────────────────────────────────

function assertCompanyId(companyId) {
  if (!companyId || typeof companyId !== 'string') {
    throw new Error('workLocationService: companyId is required');
  }
}

function validateName(name) {
  if (!name || typeof name !== 'string' || name.trim().length === 0) {
    throw new Error('workLocationService: name is required');
  }
}

// ─── Work Locations ───────────────────────────────────────────────────────────

/**
 * Create a new work location in the given company.
 *
 * @param {Object} params
 * @param {string} params.companyId
 * @param {string} params.name - Site name (required)
 * @param {string} [params.address] - Full street address (optional)
 * @param {string} params.createdBy - UID of the authenticated employer
 * @returns {Promise<Object>} The created work location including its generated id
 */
export async function createWorkLocation({ companyId, name, address = null, createdBy }) {
  assertCompanyId(companyId);
  validateName(name);
  if (!createdBy || typeof createdBy !== 'string') {
    throw new Error('workLocationService: createdBy (userId) is required');
  }

  const { database } = getFirebase();
  const newRef = push(ref(database, workLocationsPath(companyId)));
  const locationId = newRef.key;
  const now = new Date().toISOString();

  const location = {
    id: locationId,
    companyId,
    name: name.trim(),
    address: address ? address.trim() : null,
    createdBy,
    createdAt: now,
    updatedAt: now,
  };

  await set(newRef, location);
  return location;
}

/**
 * List work locations for a company, sorted by createdAt descending (newest first).
 *
 * @param {string} companyId
 * @returns {Promise<Object[]>}
 */
export async function listWorkLocations(companyId) {
  assertCompanyId(companyId);

  const { database } = getFirebase();
  const q = query(ref(database, workLocationsPath(companyId)), orderByChild('createdAt'));
  const snap = await get(q);

  if (!snap.exists()) return [];

  const all = Object.values(snap.val());
  // Newest first, tolerant of both createdAt shapes — see utils/recordDates.
  all.sort(byNewestCreated);
  return all;
}

/**
 * Get active work locations for a company (isActive !== false).
 *
 * @param {string} companyId
 * @returns {Promise<Object[]>}
 */
export const getWorkLocations = async (companyId) => {
  try {
    const { database } = getFirebase();

    const locationsRef = ref(database, workLocationsPath(companyId));
    const snapshot = await get(locationsRef);

    if (snapshot.exists()) {
      const locations = snapshot.val();
      return Object.values(locations).filter(loc => loc.isActive !== false);
    }
    return [];
  } catch (error) {
    console.error('Error getting work locations:', error);
    throw error;
  }
};

/**
 * Update fields on an existing work location.
 *
 * @param {string} locationId
 * @param {Object} updates
 * @param {string} companyId
 * @returns {Promise<Object>}
 */
export const updateWorkLocation = async (locationId, updates, companyId) => {
  try {
    const { database } = getFirebase();

    const locationRef = ref(database, workLocationPath(companyId, locationId));

    await update(locationRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });

    dbg('Work location updated:', locationId);
    return { id: locationId, ...updates };
  } catch (error) {
    console.error('Error updating work location:', error);
    throw error;
  }
};

/**
 * Soft-delete a work location (marks isActive: false).
 *
 * @param {string} locationId
 * @param {string} companyId
 * @returns {Promise<boolean>}
 */
export const deleteWorkLocation = async (locationId, companyId) => {
  try {
    const { database } = getFirebase();

    const locationRef = ref(database, workLocationPath(companyId, locationId));

    // Soft delete - just mark as inactive
    await update(locationRef, {
      isActive: false,
      deletedAt: new Date().toISOString()
    });

    dbg('Work location deleted:', locationId);
    return true;
  } catch (error) {
    console.error('Error deleting work location:', error);
    throw error;
  }
};

// ─── Employee Groups ──────────────────────────────────────────────────────────

/**
 * Create a new employee group/department.
 *
 * @param {Object} groupData
 * @param {string} companyId
 * @returns {Promise<Object>}
 */
export const createEmployeeGroup = async (groupData, companyId) => {
  try {
    const { database } = getFirebase();

    if (!companyId) {
      throw new Error('companyId is required to create a group');
    }

    // Always use company-specific path (even for 'legacy' company)
    const groupsRef = ref(database, employeeGroupsPath(companyId));
    const newGroupRef = push(groupsRef);

    const groupToStore = {
      id: newGroupRef.key,
      name: groupData.name,
      description: groupData.description || null,
      color: groupData.color || '#6B7280', // default gray
      sortOrder: groupData.sortOrder || 0,
      isActive: groupData.isActive !== false,
      companyId: companyId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: groupData.createdBy || null
    };

    await set(newGroupRef, groupToStore);
    dbg('Employee group created:', groupToStore.name);
    return groupToStore;
  } catch (error) {
    console.error('Error creating employee group:', error);
    throw error;
  }
};

/**
 * Get all active employee groups for a company, sorted by sortOrder.
 *
 * @param {string} companyId
 * @returns {Promise<Object[]>}
 */
export const getEmployeeGroups = async (companyId) => {
  try {
    const { database } = getFirebase();

    if (!companyId) {
      console.warn('[getEmployeeGroups] No companyId provided, returning empty array');
      return [];
    }

    // Always use company-specific path (even for 'legacy' company)
    const groupsRef = ref(database, employeeGroupsPath(companyId));
    const snapshot = await get(groupsRef);

    if (snapshot.exists()) {
      const groups = snapshot.val();
      return Object.values(groups)
        .filter(group => group.isActive !== false)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    }
    return [];
  } catch (error) {
    console.error('Error getting employee groups:', error);
    throw error;
  }
};

/**
 * Update fields on an existing employee group.
 *
 * @param {string} groupId
 * @param {Object} updates
 * @param {string} companyId
 * @returns {Promise<Object>}
 */
export const updateEmployeeGroup = async (groupId, updates, companyId) => {
  try {
    const { database } = getFirebase();

    if (!companyId) {
      throw new Error('companyId is required to update a group');
    }

    // Always use company-specific path (even for 'legacy' company)
    const groupRef = ref(database, employeeGroupPath(companyId, groupId));

    await update(groupRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });

    dbg('Employee group updated:', groupId);
    return { id: groupId, ...updates };
  } catch (error) {
    console.error('Error updating employee group:', error);
    throw error;
  }
};

/**
 * Soft-delete an employee group (marks isActive: false).
 *
 * @param {string} groupId
 * @param {string} companyId
 * @returns {Promise<Object>}
 */
export const deleteEmployeeGroup = async (groupId, companyId) => {
  try {
    const { database } = getFirebase();

    if (!companyId) {
      throw new Error('companyId is required to delete a group');
    }

    // Always use company-specific path (even for 'legacy' company)
    const groupRef = ref(database, employeeGroupPath(companyId, groupId));

    // Soft delete - just mark as inactive
    await update(groupRef, {
      isActive: false,
      deletedAt: new Date().toISOString()
    });

    dbg('Employee group deleted (soft):', groupId);
    return { id: groupId, deleted: true };
  } catch (error) {
    console.error('Error deleting employee group:', error);
    throw error;
  }
};

/**
 * Rename a crew AND re-point every member whose user.group === oldName.
 * Membership is stored by crew NAME on the user, so a rename must move members
 * or they would silently fall out of the crew. One multi-path update: the crew
 * and its members change together or not at all.
 * Mirrors mobile userService.renameEmployeeGroup.
 *
 * @param {string} groupId
 * @param {string} oldName
 * @param {string} newName
 * @param {string} companyId
 * @returns {Promise<{id: string, name: string}>}
 */
export const renameEmployeeGroup = async (groupId, oldName, newName, companyId) => {
  if (!companyId || !groupId) throw new Error('companyId and groupId are required');
  const trimmed = (newName || '').trim();
  if (!trimmed) throw new Error('A crew name is required');
  const { database } = getFirebase();
  const now = new Date().toISOString();

  const updates = {};
  updates[`${employeeGroupPath(companyId, groupId)}/name`] = trimmed;
  updates[`${employeeGroupPath(companyId, groupId)}/updatedAt`] = now;

  if (oldName && oldName !== trimmed) {
    const snapshot = await get(ref(database, `companies/${companyId}/users`));
    const users = snapshot.val() || {};
    Object.entries(users).forEach(([uid, u]) => {
      if (u && u.group === oldName) {
        updates[`companies/${companyId}/users/${uid}/group`] = trimmed;
        updates[`companies/${companyId}/users/${uid}/updatedAt`] = now;
      }
    });
  }

  await update(ref(database), updates);
  return { id: groupId, name: trimmed };
};

/**
 * Remove a crew and move its members out of it (group = null), in one
 * multi-path update. Unlike deleteEmployeeGroup (a soft delete that leaves
 * members pointing at a crew that no longer lists), nobody is left stranded.
 * Mirrors mobile userService.deleteEmployeeGroup.
 *
 * @param {string} groupId
 * @param {string} groupName
 * @param {string} companyId
 * @returns {Promise<{id: string}>}
 */
export const removeEmployeeGroup = async (groupId, groupName, companyId) => {
  if (!companyId || !groupId) throw new Error('companyId and groupId are required');
  const { database } = getFirebase();
  const now = new Date().toISOString();

  const updates = {};
  updates[employeeGroupPath(companyId, groupId)] = null;
  if (groupName) {
    const snapshot = await get(ref(database, `companies/${companyId}/users`));
    const users = snapshot.val() || {};
    Object.entries(users).forEach(([uid, u]) => {
      if (u && u.group === groupName) {
        updates[`companies/${companyId}/users/${uid}/group`] = null;
        updates[`companies/${companyId}/users/${uid}/updatedAt`] = now;
      }
    });
  }

  await update(ref(database), updates);
  return { id: groupId };
};

/**
 * Update an employee's group assignment.
 *
 * @param {string} employeeId
 * @param {string|null} groupName
 * @param {string} companyId
 * @returns {Promise<Object>}
 */
export const updateEmployeeGroup_Assignment = async (employeeId, groupName, companyId) => {
  try {
    const { database } = getFirebase();

    if (!companyId) {
      throw new Error('companyId is required to update group assignment');
    }

    // Always use company-specific path (even for 'legacy' company)
    const userPath = `companies/${companyId}/users/${employeeId}`;

    const userRef = ref(database, userPath);

    await update(userRef, {
      group: groupName || null,
      updatedAt: new Date().toISOString()
    });

    dbg('Employee group assignment updated:', employeeId, '->', groupName);
    return { employeeId, group: groupName };
  } catch (error) {
    console.error('Error updating employee group assignment:', error);
    throw error;
  }
};
